// realProgramme.ts — adapts the generated skeleton (content/renurture-manifest
// → programme.generated.json) into the app's Programme shape.
//
// The generated file is course → module → lesson. The app works on a flat list
// of modules (ordered across all courses) plus a `courses` grouping for the
// course-aware Journey. Lesson titles arrive as plain strings and are wrapped
// into the DisplayTitle shape the UI expects.

import type { Programme, Module, Step, CourseRef, DisplayTitle, StepType } from '@/types';
import generated from './programme.generated.json';
import { AHA_MOMENTS } from '@/config/uiFlags';

interface GenStep {
  id: string;
  module_id: string;
  course_id: string;
  title: string;
  order: number;
  type: string;
  source_type?: string;
  status?: string;
  video_url?: string;
  video_ref?: string;
  poster_url?: string;
  image_url?: string;
  image_layout?: 'above' | 'below';
  audio_url?: string;
  body?: string[];
  body_md?: string;
  transcript?: string[];
  reflection_prompt?: string;
  video_reflection?: string;
  read_minutes?: number;
  link_url?: string;
  link_label?: string;
  aha?: boolean;
  journal?: boolean;
}
interface GenModule { id: string; title: string; order: number; steps: GenStep[] }
interface GenCourse { id: string; title: string; fullTitle?: string; order: number; modules: GenModule[] }
interface GenProgramme {
  id: string;
  title: string;
  description: string;
  author?: { name: string; role: string };
  courses: GenCourse[];
}

const g = generated as unknown as GenProgramme;

/** Plain string → DisplayTitle (no emphasised word for real, descriptive titles).
 *  Strips any internal lesson-number prefix like "1.1 " / "3.2.1 ". */
const toTitle = (s: string): DisplayTitle => ({ pre: s.replace(/^\s*\d+(?:\.\d+)+\s+/, ''), em: '' });

const STEP_TYPES = new Set<StepType>(['video', 'article', 'reflection', 'exercise', 'audio', 'mixed']);
const asType = (t: string): StepType => (STEP_TYPES.has(t as StepType) ? (t as StepType) : 'mixed');

function buildProgramme(): Programme {
  const modules: Module[] = [];
  const courses: CourseRef[] = [];
  let globalOrder = 0;

  for (const course of [...g.courses].sort((a, b) => a.order - b.order)) {
    const moduleIds: string[] = [];
    for (const m of [...course.modules].sort((a, b) => a.order - b.order)) {
      globalOrder += 1;
      const steps: Step[] = m.steps
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((s) => {
          const step: Step = {
            id: s.id,
            module_id: m.id,
            title: toTitle(s.title),
            order: s.order,
            type: asType(s.type),
            completion_required: true,
          };
          if (s.video_url) step.video_url = s.video_url;
          if (s.video_ref) step.video_ref = s.video_ref;
          if (s.poster_url) step.poster_url = s.poster_url;
          if (s.image_url) step.image_url = s.image_url;
          if (s.image_layout) step.image_layout = s.image_layout;
          if (s.audio_url) step.audio_url = s.audio_url;
          if (s.body && s.body.length) step.body = s.body;
          if (s.body_md) step.body_md = s.body_md;
          if (s.transcript && s.transcript.length) step.transcript = s.transcript;
          if (s.reflection_prompt) step.reflection_prompt = s.reflection_prompt;
          if (s.video_reflection) step.video_reflection = s.video_reflection;
          if (s.read_minutes) step.read_minutes = s.read_minutes;
          if (s.link_url) step.link_url = s.link_url;
          if (s.link_label) step.link_label = s.link_label;
          if (AHA_MOMENTS && s.aha) step.aha = true;
          if (s.journal) step.journal = true;
          return step;
        });
      modules.push({
        id: m.id,
        title: toTitle(m.title),
        description: '',
        order: globalOrder,
        steps,
        course_id: course.id,
      });
      moduleIds.push(m.id);
    }
    courses.push({ id: course.id, title: course.title, fullTitle: course.fullTitle, order: course.order, moduleIds });
  }

  return {
    id: g.id,
    title: g.title,
    description: g.description,
    author: g.author,
    modules,
    courses,
  };
}

export const REAL_PROGRAMME: Programme = buildProgramme();
